import { Injectable } from '@angular/core';
import { Observable, catchError, from, of } from 'rxjs';

import {
  injectFetchUserObjectsByIds,
  injectSearchUserObjects,
} from 'src/app/api/queries/object.queries';
import { UserObjectSearchCriteria } from 'src/app/api/services/user-api.service';

import { DataObject } from '../models/core.models';

/**
 * The single door to the user's objects-by-id.
 *
 * Observable rather than promise-returning because every call site is an RxJS
 * pipeline (a dialog's `closed` stream, a form's id subject). The read *is* the
 * fetch, so callers wait for the response rather than sampling a store that may not
 * hold the rows yet.
 */
@Injectable({ providedIn: 'root' })
export class UserObjectsService {
  private readonly fetchByIds = injectFetchUserObjectsByIds();
  private readonly searchObjects = injectSearchUserObjects();

  /**
   * The given objects, in the given order, cache-first.
   *
   * `forceRefresh` bypasses the cache for all of them - the datasource selector uses
   * it when it needs the server's current rows rather than remembered ones.
   */
  objectsByIds$(
    objectIds: string[],
    forceRefresh = false,
  ): Observable<DataObject[]> {
    return from(this.fetchByIds(objectIds, forceRefresh));
  }

  /**
   * A one-shot, uncached object search, for the callers that fetch on a keystroke or
   * a tab activation rather than rendering a shared list.
   *
   * A failed search degrades to an empty list, which is why none of those call sites
   * has an error path.
   */
  search$(criteria: UserObjectSearchCriteria): Observable<DataObject[]> {
    return from(this.searchObjects(criteria)).pipe(
      catchError((error) => {
        console.error('Error fetching user objects:', error);
        return of([] as DataObject[]);
      }),
    );
  }
}
